import { useMemo } from 'react'
import {
  ComposedChart,
  Bar,
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { TrendingUp, Gauge, Banknote, Wallet, CalendarClock, Route, Users } from 'lucide-react'
import { Spinner } from '../../lib/shadcn/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import {
  type VehiclePerformanceRow,
  formatZar,
  formatZarCompact,
  formatNumber,
  formatInt,
} from '../../utils/statements'
import { OperationalStateChip } from './StateChips'

function SummaryCard({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: typeof Banknote
  label: string
  value: string
  sub?: string
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <p className="mt-1 text-lg font-semibold tabular-nums text-foreground">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  )
}

function ChartTooltip({ active, payload, label, money }: any) {
  if (!active || !payload || payload.length === 0) return null
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium text-popover-foreground">{label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
            {p.name}
          </span>
          <span className="font-medium tabular-nums text-popover-foreground">
            {money ? formatZar(p.value) : formatNumber(p.value, 2)}
          </span>
        </div>
      ))}
    </div>
  )
}

export function PerformanceTab({
  rows,
  loading,
  error,
}: {
  rows: VehiclePerformanceRow[]
  loading: boolean
  error: string | null
}) {
  const summary = useMemo(() => {
    let earnings = 0
    let net = 0
    let shifts = 0
    let km = 0
    let bestEps = -Infinity
    let bestEpsLabel = '—'
    let peakDrivers = 0
    for (const r of rows) {
      // Lifetime totals add the months that carry a figure; a month with a
      // missing figure adds nothing rather than a pretend zero (B16).
      earnings += r.earnings_zar ?? 0
      net += r.net_zar ?? 0
      shifts += r.shifts_count
      km += r.kilometres ?? 0
      if (r.earnings_per_shift_zar !== null && r.earnings_per_shift_zar > bestEps) {
        bestEps = r.earnings_per_shift_zar
        bestEpsLabel = r.period_label
      }
      if (r.drivers_count > peakDrivers) peakDrivers = r.drivers_count
    }
    return {
      earnings,
      net,
      shifts,
      km,
      bestEps: bestEps === -Infinity ? null : bestEps,
      bestEpsLabel,
      peakDrivers,
    }
  }, [rows])

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Loading performance…
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error}
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-sm text-muted-foreground">
        <Gauge className="h-6 w-6" />
        No performance data for this vehicle.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <SummaryCard icon={Banknote} label="Lifetime earnings" value={formatZar(summary.earnings)} />
        <SummaryCard icon={Wallet} label="Lifetime net" value={formatZar(summary.net)} />
        <SummaryCard icon={CalendarClock} label="Total shifts" value={formatInt(summary.shifts)} />
        <SummaryCard icon={Route} label="Total km" value={formatInt(summary.km)} />
        <SummaryCard
          icon={TrendingUp}
          label="Best earnings/shift"
          value={formatZar(summary.bestEps)}
          sub={summary.bestEpsLabel}
        />
        <SummaryCard icon={Users} label="Peak drivers" value={formatInt(summary.peakDrivers)} />
      </div>

      {/* Earnings vs net */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold text-foreground">Earnings &amp; net over time</h3>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis
              dataKey="period_label"
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              minTickGap={12}
            />
            <YAxis
              tickFormatter={formatZarCompact}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={64}
            />
            <Tooltip content={<ChartTooltip money />} cursor={{ fill: 'hsl(var(--accent))', opacity: 0.4 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="earnings_zar" name="Earnings" fill="hsl(var(--chart-1))" radius={[3, 3, 0, 0]} maxBarSize={40} />
            <Line type="monotone" dataKey="net_zar" name="Net" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Efficiency */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold text-foreground">Earnings efficiency</h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis
              dataKey="period_label"
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              minTickGap={12}
            />
            <YAxis
              yAxisId="shift"
              tickFormatter={formatZarCompact}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={64}
            />
            <YAxis
              yAxisId="km"
              orientation="right"
              tickFormatter={(v) => formatNumber(Number(v), 1)}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={48}
            />
            <Tooltip content={<ChartTooltip money />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line
              yAxisId="shift"
              type="monotone"
              dataKey="earnings_per_shift_zar"
              name="Earnings / shift"
              stroke="hsl(var(--chart-3))"
              strokeWidth={2}
              dot={false}
            />
            <Line
              yAxisId="km"
              type="monotone"
              dataKey="earnings_per_km_zar"
              name="Earnings / km"
              stroke="hsl(var(--chart-4))"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly table */}
      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-4 py-2.5 text-sm font-semibold text-foreground">
          Monthly detail
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Period</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-right">Drivers</TableHead>
                <TableHead className="text-right">Shifts</TableHead>
                <TableHead className="text-right">Days</TableHead>
                <TableHead className="text-right">Km</TableHead>
                <TableHead className="text-right">Earn/shift</TableHead>
                <TableHead className="text-right">Earn/km</TableHead>
                <TableHead className="text-right">Net/km</TableHead>
                <TableHead className="text-right">Petrol</TableHead>
                <TableHead className="text-right">Service</TableHead>
                <TableHead className="text-right">Maint.</TableHead>
                <TableHead className="text-right">Minor rep.</TableHead>
                <TableHead className="text-right">Carwash</TableHead>
                <TableHead className="text-right">Insurance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.period_start}>
                  <TableCell className="whitespace-nowrap font-medium">{r.period_label}</TableCell>
                  <TableCell>
                    <OperationalStateChip state={r.operational_state} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatInt(r.drivers_count)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumber(r.shifts_count)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumber(r.days_count)}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.kilometres === null ? '—' : formatInt(r.kilometres)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatZar(r.earnings_per_shift_zar)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatZar(r.earnings_per_km_zar)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatZar(r.net_per_km_zar)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatZar(r.petrol_zar)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatZar(r.service_cost_zar)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatZar(r.maintenance_cost_zar)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatZar(r.minor_repairs_zar)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatZar(r.carwashes_zar)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatZar(r.insurance_zar)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
}
